import { subscribeToAgentForceStartEvent } from './customerServiceIntegration/subscribeToAgentForceStartEvent'
import { subscribeToMIAWStartEvent } from './customerServiceIntegration/subscribeToMIAWStartEvent'
import getConfig from './miawConfig'
import { EmbeddedServiceBootstrap, BotType, MiawConfig } from './types'
import { setBotType } from './utils'
import loadScript from './utils/loadScript'
import { logDevelopmentMessage } from './utils/logDevelopmentMessage'
import { dispatchOmnitureEvent } from './utils/general'
import { getPageStatus } from './customerServiceIntegration/getPageStatus'

declare var embeddedservice_bootstrap: EmbeddedServiceBootstrap

type UserData = {
  email?: string
  shopperId?: string
  name?: string
}

let botType: BotType = BotType.DEFAULT

const handleConversationRouted = (event: CustomEvent) => {
  const payload = JSON.parse(event.detail.conversationEntry.entryPayload)
  if (payload.routingType === 'Transfer') {
    dispatchOmnitureEvent('dataStateChange', {
      action: 'messaging',
      type: 'established',
    })
  }
}

const handleConversationStarted = () => {
  setBotType(botType)
  dispatchOmnitureEvent('dataStateChange', {
    action: 'messaging',
    type: 'start',
  })
}

async function initializeMIAW(_config: MiawConfig) {
  const userData = await fetchUserData()
  logDevelopmentMessage(`Initialising:: ${_config.eswConfigDevName}`)
  try {
    window.addEventListener('onEmbeddedMessagingReady', () => {
      configurePrechatFields(userData)
    })

    window.removeEventListener(
      'onEmbeddedMessagingConversationStarted',
      handleConversationStarted as unknown as EventListener
    )
    window.addEventListener('onEmbeddedMessagingConversationStarted', handleConversationStarted)

    window.removeEventListener('onEmbeddedMessagingConversationRouted', handleConversationRouted as EventListener)
    window.addEventListener('onEmbeddedMessagingConversationRouted', handleConversationRouted as EventListener)

    if (embeddedservice_bootstrap) {
      embeddedservice_bootstrap.settings.language = 'en_US'
      embeddedservice_bootstrap.settings.hideChatButtonOnLoad = _config.hideChatButtonOnLoad || getPageStatus().isInCustomerServicePage
      embeddedservice_bootstrap.init(_config.orgID, _config.eswConfigDevName, _config.siteURL, {
        scrt2URL: _config.snippetConfigURL,
      })
    }
  } catch (err) {
    logDevelopmentMessage(`[MIAW] Error initializing MIAW chat:${err}`)
    throw new Error(`[MIAW] initialization error: ${(err as Error).message}`)
  }
}

async function fetchUserData(): Promise<UserData> {
  const userData = (await window.auth0?.getUser()) || {}
  return {
    ...userData,
    shopperId: userData['https://www.johnlewis.com/auth/customerId'] || '',
  }
}

function configurePrechatFields(userData: UserData) {
  if (embeddedservice_bootstrap) {
    embeddedservice_bootstrap.prechatAPI?.setHiddenPrechatFields({
      IDV_verification_tc: JSON.stringify({
        ...(userData.shopperId && { shopperId: userData.shopperId }),
      }),
      ChatStartPageURL: window.location.href.slice(0, 255),
    })
    embeddedservice_bootstrap.prechatAPI?.setVisiblePrechatFields({
      _firstName: {
        value: userData?.name || '',
        isEditableByEndUser: true,
      },
      _email: {
        value: userData?.email || '',
        isEditableByEndUser: userData.shopperId === '',
      },
    })
  }
}

export async function loadMIAW(_type = BotType.DEFAULT) {
  botType = _type

  const eventSubscriptions: { [key in BotType]?: () => void } = {
    [BotType.DEFAULT]: subscribeToMIAWStartEvent,
    [BotType.AGENT_FORCE]: subscribeToAgentForceStartEvent,
  }

  try {
    const config = getConfig(BotType.DEFAULT)
    await loadScript(`${config.siteURL}/assets/js/bootstrap.min.js`)
    await initializeMIAW(config)
    eventSubscriptions[_type]?.()
  } catch (err) {
    console.error(`[${_type}] Failed to load MIAW:`, err)
  }
}
